import { useCallback, useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import CustomTooltip from './components/customTooltip/CustomTooltip';
import './chart.css';
import { useNavigate } from 'react-router-dom';
import {
  CFR_CHART_KEYS,
  CONTRIBUTOR_CHART_KEYS,
  CUSTOM_CHART_KEYS,
  DEPENDENCY_VULNERABILITIES_CHART_KEYS,
  DEPLOY_CHART_KEYS,
  DOCUMENT_UPDATED_CHARTS,
  GOAL_METRIC_GIT_KEYS,
  GOAL_METRIC_JIRA_KEYS,
  ISSUE_CHART_KEYS,
  ISSUE_LIFECYCLE_DIST_CHARTS,
  MEETING_CHARTS,
  PR_CHART_KEYS,
  PR_HEALTH_INDEX_CHARTS,
  QUALITY_CHART_KEYS,
  REQUIREMENT_STABILITY_CHARTS,
  splitTime,
  SPRINT_CHARTS,
  TEST_DETAIL_CHARTS,
  TEST_RUN_CHARTS,
} from '../shared/utils';
import MetricSidebar from './components/MetricSidebar';
import moment from 'moment';
import { WELLBEING_CHART_KEYS } from '../shared/utils/index';
import { useTheme, Box } from '@chakra-ui/react';
import CustomLegend from '../customLegend';
import { DateRangeSelection } from '../shared/typings';

export interface LineChartDataItem {
  xLabelName: string;
  endDate: string;
  value: string;
  startDate: string;
  xLabelId?: string;
  yRollingAvg?: number;
  [key: string]: string | number | undefined;
}

export interface LineChartKey {
  key: string;
  name: string;
  color: string;
  chartType: string | null;
}

interface LineChartRechartsProps {
  chartref?: any;
  data: LineChartDataItem[];
  keys?: LineChartKey[];
  chartMetadata?: any;
  showLabel?: boolean;
  selectedTeam: string;
  selected?: DateRangeSelection;
  hideBtn?: boolean;
  slant?: boolean;
  granularSlant?: boolean;
  ticketType?: string;
  forDetails?: boolean;
  queryKey?: string;
  repos?: string[];
  showYLabel?: boolean;
  showXLabel?: boolean;
  showCustomLegend?: boolean;
  detailView?: boolean;
}

//This component is used to show line Requirement Stability, Code Assistance Active users, Code Assistance Lines Accepted, Code Assistance Chat Acceptance and in most of the metrics
export const LineChartRecharts = ({
  chartref,
  data,
  keys,
  chartMetadata,
  showLabel,
  selectedTeam,
  selected,
  hideBtn,
  slant,
  granularSlant,
  ticketType,
  forDetails,
  queryKey,
  repos,
  showYLabel,
  showCustomLegend,
  showXLabel,
  detailView,
}: LineChartRechartsProps) => {
  const { colors } = useTheme();
  const [hoveredLabel, setHoveredLabel] = useState<any>(null);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [clickedData, setClickedData] = useState<any>(null);
  const [currentChartMeta, setCurrentChartMeta] = useState<any>(null);
  const [barProps, setBarProps] = useState<any>({});

  useEffect(() => {
    setBarProps(
      keys?.reduce(
        (a: any, { key }: LineChartKey) => {
          a[key] = true;
          return a;
        },
        {
          hover: null,
        }
      )
    );
  }, [keys]);

  const params = new URLSearchParams(window.location.search);
  const team = params.get('team');

  const handleModalClose = useCallback(() => {
    setIsOpen(false);
  }, [isOpen]);

  const handleModalOpen = useCallback(() => {
    setIsOpen(true);
  }, [isOpen]);

  const handleClick = (label: { value: string }) => {
    const foundItem = data?.find((el) => el.xLabelName === label.value);
    if (foundItem?.xLabelId) {
      navigate(`/sprint/${foundItem.xLabelId}${team ? `?team=${team}` : ''}`);
    }
  };

  const handleMouseEnter = (label: any) => {
    setHoveredLabel(label);
  };

  const handleMouseLeave = () => {
    setHoveredLabel(null);
  };

  const renderCustomTick = (props: any) => {
    const { x, y, payload } = props;
    const { value } = payload;
    const isHovered = value === hoveredLabel;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={4}
          fill={colors.primary}
          fontSize={7.5}
          fontWeight={isHovered ? 700 : 600}
          cursor="pointer"
          textAnchor="end"
          textDecoration={'underline'}
          onMouseEnter={() => handleMouseEnter(value)}
          onMouseLeave={handleMouseLeave}
          transform="rotate(-20)"
        >
          {value}
        </text>
      </g>
    );
  };

  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const [hideTooltip, setHideTooltip] = useState(false);

  const handleTooltipHoverChange = (hovered: boolean) => {
    setIsTooltipHovered(hovered);
  };

  return (
    <Box style={{ position: 'relative', width: '100%', height: '100%' }}>
      <ResponsiveContainer ref={chartref} width="100%" height="100%">
        <LineChart
          data={data}
          margin={{
            top: 10,
            left: showLabel || showYLabel ? 0 : slant ? 108 : -5,
            bottom: granularSlant || slant ? 52 : 0,
          }}
          onMouseEnter={() => {
            handleTooltipHoverChange(false);
            setHideTooltip(false);
          }}
          onMouseLeave={() => {
            handleTooltipHoverChange(true);
            setHideTooltip(true);
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey={chartMetadata.xAxis}
            axisLine={false}
            tickLine={false}
            style={{
              fontSize: 7.5,
              fontWeight: 500,
              cursor: slant ? 'pointer' : undefined,
            }}
            height={granularSlant || slant ? 55 : undefined}
            angle={slant || granularSlant ? -60 : undefined}
            textAnchor={granularSlant || slant ? 'end' : 'middle'}
            interval={granularSlant || slant ? 0 : undefined}
            onClick={slant ? handleClick : undefined}
            tick={slant ? renderCustomTick : undefined}
            label={
              showXLabel
                ? {
                    value: chartMetadata.xlabel,
                    fontSize: 11,
                    fontWeight: 400,
                    position: 'insideBottom',
                    dy: detailView ? 40 : 5,
                  }
                : {}
            }
          />

          <YAxis
            tickFormatter={(value) => {
              if (chartMetadata.ylabel === 'Hour') {
                return splitTime(value);
              } else if (chartMetadata.dataType === 'TIME') {
                return splitTime(value / 60);
              } else {
                if (value >= 1000000) {
                  return `${(value / 1000000).toFixed(1)}M`;
                } else if (value >= 1000) {
                  return `${(value / 1000).toFixed(1)}K`;
                }
                return value;
              }
            }}
            label={
              !showYLabel
                ? {}
                : {
                    value: chartMetadata.ylabel,
                    angle: -90,
                    position: 'insideLeft',
                    fontSize: 11,
                    fontWeight: 400,
                    dy: 40,
                  }
            }
            axisLine={false}
            tickLine={false}
            style={{ fontSize: 11, fontWeight: 400 }}
          />

          <Tooltip
            content={
              <CustomTooltip
                handleModalOpen={handleModalOpen}
                setClickedData={setClickedData}
                unit={chartMetadata.ylabel}
                onClickAction={() => {
                  setCurrentChartMeta(chartMetadata);
                }}
                chartKey={chartMetadata?.chartKey}
                hideTooltip={hideTooltip}
                hideBtn={hideBtn}
                onHoverChange={handleTooltipHoverChange}
              />
            }
            trigger={isTooltipHovered ? 'click' : 'hover'}
            wrapperStyle={{ outline: 'none' }}
            position={{ y: -10 }}
            allowEscapeViewBox={{ x: false, y: false }}
          />

          {showLabel && (
            <Legend
              verticalAlign="middle"
              layout="vertical"
              iconType="circle"
              iconSize={11}
              align="right"
              content={
                <CustomLegend
                  keys={keys}
                  barProps={barProps}
                  setBarProps={setBarProps}
                  w={170}
                />
              }
              wrapperStyle={{
                right: -8,
                fontSize: 11.5,
                top: 0,
              }}
            />
          )}

          {keys?.map((el) =>
            el.key === 'yRollingAvg' || el.chartType === 'dashed_line_chart' ? (
              <Line
                key={el.key}
                type="monotone"
                dataKey={el.key}
                name={el.name}
                stroke={el.color}
                dot={false}
                activeDot={false}
                strokeWidth={2}
                strokeDasharray="3 6"
                opacity={barProps.hover && barProps.hover !== el.key ? 0.25 : 1}
                hide={barProps[el.key as string] === false}
              />
            ) : (
              <Line
                key={el.key}
                dataKey={el.key}
                name={el.name}
                stroke={el.color}
                strokeWidth={2}
                opacity={barProps.hover && barProps.hover !== el.key ? 0.25 : 1}
                hide={barProps[el.key as string] === false}
                activeDot={
                  [
                    'SPRINT_ADDITION_RATE',
                    'SPRINT_COMPLETION_PERCENT',
                    'SPRINT_VELOCITY',
                  ].includes(chartMetadata.chartKey)
                    ? { r: 6, strokeWidth: 3, stroke: '#9DB2BF' }
                    : {
                        r: 6,
                        strokeWidth: 3,
                        stroke: '#9DB2BF',
                        cursor: 'pointer',
                        onClick: (event: any, payload: any) => {
                          handleModalOpen();
                          setClickedData({ ...payload, name: el.name });
                          setCurrentChartMeta(chartMetadata);
                        },
                        className: 'active-dot',
                      }
                }
                dot={{
                  fill: el.color,
                  stroke: el.color,
                  strokeWidth: 3,
                  r: 2,
                }}
              />
            )
          )}
        </LineChart>
      </ResponsiveContainer>
      {[
        ...PR_CHART_KEYS,
        ...CFR_CHART_KEYS,
        ...DEPLOY_CHART_KEYS,
        ...CONTRIBUTOR_CHART_KEYS,
        ...ISSUE_CHART_KEYS,
        ...GOAL_METRIC_GIT_KEYS,
        ...GOAL_METRIC_JIRA_KEYS,
        ...WELLBEING_CHART_KEYS,
        ...QUALITY_CHART_KEYS,
        ...CUSTOM_CHART_KEYS,
        ...REQUIREMENT_STABILITY_CHARTS,
        ...DEPENDENCY_VULNERABILITIES_CHART_KEYS,
        ...MEETING_CHARTS,
        ...PR_HEALTH_INDEX_CHARTS,
        ...DOCUMENT_UPDATED_CHARTS,
        ...SPRINT_CHARTS,
        ...ISSUE_LIFECYCLE_DIST_CHARTS,
        ...TEST_RUN_CHARTS,
        ...TEST_DETAIL_CHARTS,
        'COMMIT_COUNT',
      ].includes(currentChartMeta?.chartKey) &&
        isOpen && (
          <MetricSidebar
            querykey={queryKey}
            openDrawer={isOpen}
            handleDrawerClose={handleModalClose}
            heading={chartMetadata.chartTitle}
            subHeading={
              currentChartMeta?.chartKey === REQUIREMENT_STABILITY_CHARTS[0]
                ? `${moment(clickedData[0]?.payload?.xLabelName).format(
                    'DD MMM'
                  )}`
                : `${
                    Array.isArray(clickedData)
                      ? moment(clickedData[0]?.payload?.startDate).format(
                          'DD MMM'
                        )
                      : moment(clickedData?.payload?.startDate).format('DD MMM')
                  } - ${
                    Array.isArray(clickedData)
                      ? moment(clickedData[0]?.payload?.endDate).format(
                          'DD MMM'
                        )
                      : moment(clickedData?.payload?.endDate).format('DD MMM')
                  }${
                    QUALITY_CHART_KEYS?.includes(currentChartMeta?.chartKey)
                      ? `, ${clickedData?.name} ${
                          clickedData?.value ? `(${clickedData?.value})` : ''
                        }`
                      : clickedData || (clickedData && clickedData[0])
                      ? `${
                          clickedData[0]?.name === 'Hours' ||
                          clickedData?.name === 'Hours'
                            ? ''
                            : `, ${clickedData[0]?.name || clickedData?.name}${
                                clickedData[0]?.payload?.y ||
                                clickedData?.payload?.y
                                  ? ['TEST_RUN_TIME'].includes(
                                      currentChartMeta?.chartKey
                                    )
                                    ? ` (${
                                        (clickedData[0]?.payload?.y &&
                                          splitTime(
                                            clickedData[0]?.payload?.y
                                          )) ||
                                        (clickedData?.payload?.y &&
                                          splitTime(clickedData?.payload?.y))
                                      })`
                                    : ` (${
                                        clickedData[0]?.payload?.y ||
                                        clickedData?.payload?.y
                                      })`
                                  : ''
                              }`
                        } `
                      : ''
                  }`
            }
            payload={clickedData}
            currentChartMeta={currentChartMeta}
            filterBy={{
              teamId: selectedTeam,
              type:
                chartMetadata.chartKey === 'BUG_THROUGHPUT'
                  ? 'bug'
                  : chartMetadata.chartKey === 'PR_HEALTH_INDEXES'
                  ? 'merged_without_review'
                  : ticketType || '',
              projectsFilter: repos,
              sprintId: selected?.sprintId,
            }}
            repoTitle={repos && repos?.length >= 1}
            selectedTeam={selectedTeam}
          />
        )}
    </Box>
  );
};
